/**
 * SaasControlBar — single lean strip combining the Provider switcher and the
 * Surface switcher inline (for vendors with surfaces), separated by a divider.
 *
 * Replaces the two stacked strips (VendorSelector + SurfaceSelector) so the
 * surface tabs read as a contextual sub-menu of the Anthropic provider rather
 * than a second full-width card — reclaiming the vertical space for data.
 *
 * `showProvider={false}` keeps the Surface tabs and drops the Provider tabs,
 * for hosts that already have a provider switcher of their own — Agent Atlas
 * embeds these pages under its own FinOps surface pills. Two provider
 * switchers on one screen would not merely be redundant: this one writes
 * `?vendor=` on click, which SaasVendorContext resolves *before* the store the
 * host pinned, so the embed would silently start disagreeing with the pill
 * above it.
 */

import React from 'react';

import { useTranslation } from 'react-i18next';
import FeatherIcon from '../common/FeatherIcon';
import { useSaasVendor } from '../../contexts/SaasVendorContext';
import './vendor-selector.css';
const SURFACE_ICON = {
  all: 'grid',
  chat: 'message-circle',
  code: 'terminal',
  cowork: 'users',
  desktop: 'monitor',
  ide: 'code',
  cli: 'terminal',
  web_remote: 'globe',
};

export default function SaasControlBar({ showProvider = true }) {
  const { t } = useTranslation('saasAiUi');

  const { vendor, setVendor, availableVendors, surface, setSurface, surfaces } = useSaasVendor();
  const multiVendor = showProvider && availableVendors && availableVendors.length > 1;
  const showSurface = Array.isArray(surfaces) && surfaces.length > 0;
  if (!multiVendor && !showSurface) return null;

  return (
    <div className="saas-control-bar" role="navigation" aria-label={t('saasAiUi:saasAiSaascontrolbar.saasAiProviderAndSurface')}>
      {multiVendor && (
        <div className="saas-control-bar__group">
          <FeatherIcon name="layers" size={16} className="saas-vendor-selector__icon" />
          <span className="saas-vendor-selector__label">{t('saasAiUi:saasAiSaascontrolbar.provider')}</span>
          <div className="saas-vendor-selector__tabs">
            {availableVendors.map((v) => (
              <button
                key={v.id}
                type="button"
                className={
                  'saas-vendor-selector__tab' +
                  (v.id === vendor ? ' saas-vendor-selector__tab--active' : '')
                }
                aria-pressed={v.id === vendor}
                onClick={() => setVendor(v.id)}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showSurface && (
        <div className="saas-control-bar__group saas-control-bar__group--surface">
          {multiVendor && <span className="saas-control-bar__divider" aria-hidden="true" />}
          <FeatherIcon name="git-branch" size={15} className="saas-vendor-selector__icon" />
          <span className="saas-vendor-selector__label">{t('saasAiUi:saasAiSaascontrolbar.surface')}</span>
          <div className="saas-vendor-selector__tabs">
            {surfaces.map((s) => (
              <button
                key={s.id}
                type="button"
                className={
                  'saas-vendor-selector__tab' +
                  (s.id === surface ? ' saas-vendor-selector__tab--active' : '')
                }
                aria-pressed={s.id === surface}
                onClick={() => setSurface(s.id)}
              >
                <FeatherIcon name={SURFACE_ICON[s.id] || 'circle'} size={13} />
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
